import { Cart } from '@/types';

function getParentTargetOrigin() {
  if (!isInIframe() || !document.referrer) {
    return window.location.origin;
  }
  try {
    return new URL(document.referrer).origin;
  } catch {
    return window.location.origin;
  }
}

function postMessageToParent(message: unknown) {
  window.parent.postMessage(message, getParentTargetOrigin());
}

export function notifyParentAboutClicks() {
  function handleClick() {
    const clickData = {
      type: 'click',
    };
    postMessageToParent(clickData);
  }

  document.addEventListener('click', handleClick);

  return () => {
    document.removeEventListener('click', handleClick);
  };
}

// adapted from https://stackoverflow.com/a/326076/8678845
export function isInIframe () {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true;
  }
}

type CartSize = {
  width: number;
  height: number;
};

const IFRAME_HEIGHT_COMPENSATION = 2;

function measureCartSize(target: HTMLElement, root: HTMLElement, body: HTMLElement): CartSize {
  const targetRect = target.getBoundingClientRect();

  return {
    width: Math.ceil(Math.max(targetRect.width, root.scrollWidth, body.scrollWidth)),
    height: Math.ceil(Math.max(targetRect.height, root.scrollHeight, body.scrollHeight))
      + IFRAME_HEIGHT_COMPENSATION,
  };
}

export function watchCartSize() {
  const target = document.querySelector<HTMLElement>('#app') ?? document.body;
  const root = document.documentElement;
  const body = document.body;
  let lastSentSize: CartSize | null = null;
  let animationFrameId: number | null = null;

  const sendResize = () => {
    animationFrameId = null;
    const size = measureCartSize(target, root, body);

    // Skip sending if dimensions haven't changed
    if (size.width === lastSentSize?.width && size.height === lastSentSize.height) {
      return;
    }

    // Update last sent dimensions
    lastSentSize = size;
    postMessageToParent({
      type: 'resize',
      newWidth: size.width,
      newHeight: size.height,
    });
  };

  const scheduleResize = (force = false) => {
    if (force) {
      lastSentSize = null;
    }
    if (animationFrameId !== null) {
      return;
    }

    // Schedule resize using requestAnimationFrame
    animationFrameId = window.requestAnimationFrame(sendResize);
  };

  const resizeObserver = new ResizeObserver(() => scheduleResize());
  const handleWindowResize = () => scheduleResize(true);

  // Observe target element
  resizeObserver.observe(target);
  // Observe body if it's not the target element
  if (target !== body) {
    resizeObserver.observe(body);
  }
  // Observe root element
  resizeObserver.observe(root);
  // Listen for window resize events
  window.addEventListener('resize', handleWindowResize);
  // Initial resize
  scheduleResize();

  return () => {
    // Clean up
    resizeObserver.disconnect();
    if (animationFrameId !== null) {
      window.cancelAnimationFrame(animationFrameId);
    }
    window.removeEventListener('resize', handleWindowResize);
  };
}

export function notifyParentAboutCartChange(cart: Cart) {
  const eventData = {
    type: 'cartChange',
    cart
  };

  postMessageToParent(eventData);
}

export function notifyParentAboutUrlChange(newUrl: string) {
  const eventData = {
    type: 'urlChange',
    newUrl
  };

  postMessageToParent(eventData);
}

export function requestIframeSizeReset() {
  const requestData = {
    type: 'resetSize',
  };

  postMessageToParent(requestData);
}
